<template>
  <ModalDialog
    :header-text="`${company.CompanyName} Chometz Submissions`"
    @close-modal="closeModal"
    open
  >
    <template v-slot:content>
      <Grid
        class="chometz-submissions-grid"
        :data-items="submissions"
        :columns="columns"
        :loading="loading"
        ref="gridRef"
        data-cy="chometz-submissions-grid"
      >
        <template v-slot:TypeCell="{ props: { dataItem } }">
          <td class="k-table-td">
            {{ getSubmissionTypeString(dataItem) }}
          </td>
        </template>
        <template v-slot:CheckCell="{ props: { dataItem, field } }">
          <td class="k-table-td">
            <font-awesome-icon
              class="no-chometz-icon"
              v-if="dataItem[field]"
              icon="check"
            />
          </td>
        </template>
      </Grid>
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import { Grid } from "@progress/kendo-vue-grid";
import { onBeforeMount, ref, watchEffect } from "vue";
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import { useMainStore } from "@/stores/useMainStore";
import { api } from "@/api";
import type { ChometzCompany } from "@/Domain/ChometzCompany";
import type { ChometzSubmission } from "@/Domain/ChometzSubmission";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { getSubmissionTypeString } from "@/shared/helpers/chometzHelper";
import GridController from "@/shared/services/GridController";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";

const main = useMainStore();

const props = defineProps<{
  company: ChometzCompany;
}>();

const emit = defineEmits(["closeModal"]);

const loading = ref(false);
const submissions = ref<ChometzSubmission[]>([]);
const gridRef = ref<InstanceType<typeof Grid> | null>(null);

const columns = ref<GridColumnPropsExtended[]>([
  {
    title: "Customer Name",
    field: "SubmissionCustomerName",
    minWidth: 90,
  },
  {
    title: "Sold through",
    cell: "TypeCell",
    field: "SubmissionType",
    minWidth: 90,
  },
  {
    title: "Company Response",
    field: "SubmissionComment",
    minWidth: 90,
  },
  {
    title: "Time zone",
    field: "CustomerTimezone",
    minWidth: 90,
  },
  {
    title: "No Chometz",
    cell: "CheckCell",
    field: "NoChometz",
    minWidth: 70,
  },
  {
    title: "Finalized",
    cell: "CheckCell",
    field: "SubmissionFinalized",
    minWidth: 90,
  },
  {
    title: "Email Sent",
    cell: "CheckCell",
    field: "EmailSent",
    minWidth: 70,
  },
  {
    title: "Company Response Submitted",
    field: "SubmittedDate",
    type: "date",
    format: "{0:MM/dd/yyyy}",
    minWidth: 90,
  },
  {
    title: "Date",
    field: "Date",
    type: "date",
    format: "{0:MM/dd/yyyy h:mm:ss a}",
    minWidth: 90,
  },
]);

const controller = new GridController<ChometzSubmission>(
  "ChometzCompanyDetailsModal",
  columns.value
);

const getSubmissionsAsync = async function () {
  handleResponsePromiseAsync(
    api.get("GetChometzSubmissionsWithFiltering", {
      userLogin: main.userLogin,
      companyLogin: !props.company.IsPlant
        ? props.company.CompanyId.toString()
        : "0",
      plantId: props.company.IsPlant ? props.company.PlantId : 0,
      salesYear: 0,
      includeNoChometz: true,
      searchValue: "",
      filters: "",
      skip: 0,
      take: 100,
      valuesForFieldOnly: "",
      sortField: "",
      ascending: false,
    }),
    (data) => {
      submissions.value = data.ListItems;
    },
    "ChometzCompanyDetailsModal.vue",
    "getSubmissionsAsync"
  );
};

const closeModal = function () {
  emit("closeModal");
};

watchEffect(() => {
  if (gridRef.value && !controller.gridRef.value) {
    controller.setGridRef(gridRef);
  }
});

onBeforeMount(() => {
  getSubmissionsAsync();
});
</script>

<style scoped lang="scss">
.chometz-submissions-grid {
  .no-chometz-icon {
    width: 100%;
  }
}
</style>
